
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface TutorialStep {
  icon: string;
  title: string;
  description: string;
  path: string;
  color: string;
}

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const highlights = [
  {
    icon: 'ri-edit-2-line',
    color: 'from-teal-500 to-emerald-500',
    bg: 'bg-teal-50 dark:bg-teal-900/30',
    border: 'border-teal-200 dark:border-teal-700',
    title: '自分で投稿できる',
    desc: '日記・資料・動画・クイズなど、自分の学習内容を自由に投稿・記録できます。',
  },
  {
    icon: 'ri-eye-line',
    color: 'from-orange-500 to-amber-500',
    bg: 'bg-orange-50 dark:bg-orange-900/30',
    border: 'border-orange-200 dark:border-orange-700',
    title: 'みんなの投稿を見られる',
    desc: '他のユーザーが投稿した日記・資料・動画・クイズを自由に閲覧できます。',
  },
  {
    icon: 'ri-trophy-line',
    color: 'from-yellow-500 to-orange-500',
    bg: 'bg-yellow-50 dark:bg-yellow-900/30',
    border: 'border-yellow-200 dark:border-yellow-700',
    title: '友達と競い合える',
    desc: 'ランキング機能で友達と勉強時間を競い合い、モチベーションを高め合えます。',
  },
];

export default function TutorialModal({ isOpen, onClose }: TutorialModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  // 0 = イントロ、1〜 = 各機能ステップ
  const INTRO_INDEX = 0;

  const steps: TutorialStep[] = [
    {
      icon: 'ri-book-2-line',
      title: '勉強日記',
      description: '毎日の学習内容・時間・気づきを記録できます。振り返りで学習効率を高め、継続的な成長をサポートします。科目ごとに整理され、いつでも過去の学習を確認できます。',
      path: '/diary',
      color: 'from-teal-500 to-emerald-600',
    },
    {
      icon: 'ri-file-text-line',
      title: '勉強資料',
      description: '科目ごとに整理した学習資料をいつでも参照できます。PDFやリンクを保存し、知識の体系化に役立てます。タグ機能で効率的に資料を検索できます。',
      path: '/materials',
      color: 'from-blue-500 to-cyan-600',
    },
    {
      icon: 'ri-video-line',
      title: '講義動画',
      description: 'YouTubeの講義動画をまとめて管理できます。科目別に整理し、効率的な動画学習をサポートします。お気に入り機能で重要な動画をすぐに見つけられます。',
      path: '/videos',
      color: 'from-purple-500 to-violet-600',
    },
    {
      icon: 'ri-question-answer-line',
      title: '一問一答クイズ',
      description: '自作の問題で繰り返し復習できます。習熟度を可視化して弱点を克服し、効率的な記憶定着を実現します。ブックマーク機能で苦手な問題を重点的に学習できます。',
      path: '/quiz',
      color: 'from-orange-500 to-amber-600',
    },
    {
      icon: 'ri-bar-chart-2-line',
      title: 'ダッシュボード',
      description: '学習時間・進捗・ストリークを一目で確認できます。グラフやチャートで学習状況を可視化し、モチベーション維持に活用します。目標設定機能で計画的な学習が可能です。',
      path: '/dashboard',
      color: 'from-rose-500 to-pink-600',
    },
    {
      icon: 'ri-microscope-line',
      title: '研究経過報告',
      description: '研究の進捗・成果・文献をまとめて管理できます。研究活動を体系的に記録し、ポートフォリオとして活用できます。研究デザインや図表も整理できます。',
      path: '/portfolio',
      color: 'from-indigo-500 to-blue-600',
    },
    {
      icon: 'ri-trophy-line',
      title: 'ランキング',
      description: '学習時間のランキングで他のユーザーと競い合えます。グループ機能でチームでの学習も可能です。モチベーションを高め、継続的な学習習慣を作ります。',
      path: '/ranking',
      color: 'from-yellow-500 to-orange-600',
    },
  ];

  // 全スライド数：イントロ1枚 + 機能ステップ
  const totalSlides = 1 + steps.length;
  // currentStep: 0=イントロ, 1〜7=機能
  const isIntro = currentStep === INTRO_INDEX;
  const stepIndex = currentStep - 1; // 機能ステップのインデックス
  const step = !isIntro ? steps[stepIndex] : null;

  const handleNext = () => {
    if (currentStep < totalSlides - 1) setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden animate-scale-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {/* Progress bar */}
        <div className="h-1 bg-gray-200 dark:bg-gray-700">
          <div
            className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-500 ease-out"
            style={{ width: `${((currentStep + 1) / totalSlides) * 100}%` }}
          ></div>
        </div>

        {/* ===== イントロスライド ===== */}
        {isIntro && (
          <div className="p-8 sm:p-10">
            {/* ドット */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    index === currentStep
                      ? 'w-8 h-2 bg-gradient-to-r from-teal-500 to-emerald-500'
                      : 'w-2 h-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400'
                  }`}
                ></button>
              ))}
            </div>

            {/* タイトル */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl shadow-lg mb-4">
                <i className="ri-book-open-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                このサイトでできること
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                3つのポイントをチェックしよう
              </p>
            </div>

            {/* 3つのハイライト */}
            <div className="flex flex-col gap-4 mb-8">
              {highlights.map((h, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-4 p-4 rounded-xl border ${h.bg} ${h.border}`}
                >
                  <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${h.color} rounded-xl shadow`}>
                    <i className={`${h.icon} text-white text-lg`}></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 dark:text-white text-sm mb-0.5">{h.title}</p>
                    <p className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed">{h.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ナビゲーション */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">1 / {totalSlides}</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors cursor-pointer"
                >
                  スキップ
                </button>
                <button
                  onClick={handleNext}
                  className="px-6 py-2.5 text-sm font-bold bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  各機能を見る
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ===== 機能ステップスライド ===== */}
        {!isIntro && step && (
          <div className="p-8 sm:p-12">
            {/* ドット */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    index === currentStep
                      ? 'w-8 h-2 bg-gradient-to-r from-teal-500 to-emerald-500'
                      : 'w-2 h-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400'
                  }`}
                ></button>
              ))}
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className={`w-20 h-20 flex items-center justify-center bg-gradient-to-br ${step.color} rounded-2xl shadow-lg`}>
                <i className={`${step.icon} text-white text-4xl`}></i>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white text-center mb-4">
              {step.title}
            </h3>

            {/* Description */}
            <p className="text-gray-600 dark:text-gray-300 text-center leading-relaxed mb-6">
              {step.description}
            </p>

            {/* Link to page */}
            <div className="flex justify-center mb-8">
              <Link
                to={step.path}
                onClick={onClose}
                className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${step.color} text-white font-semibold rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300`}
              >
                <span>このページを見る</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={handlePrev}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-1"></i>
                前へ
              </button>

              <span className="text-xs text-gray-400">{currentStep + 1} / {totalSlides}</span>

              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors cursor-pointer"
              >
                スキップ
              </button>

              {currentStep < totalSlides - 1 ? (
                <button
                  onClick={handleNext}
                  className="px-4 py-2 text-sm font-medium text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  次へ
                  <i className="ri-arrow-right-line ml-1"></i>
                </button>
              ) : (
                <button
                  onClick={onClose}
                  className="px-6 py-2 text-sm font-medium bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer whitespace-nowrap"
                >
                  完了
                  <i className="ri-check-line ml-1"></i>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
